import { createContext, type ReactNode, use, useId } from 'react'
import cx from 'classnames'
import { RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import styles from './ExpandableList.module.css'

interface DisclosureState {
  isExpanded: boolean
  toggle: () => unknown
  panelId: string
}

const DisclosureContext = createContext<DisclosureState | null>(null)

export interface ExpandableListProps {
  children?: ReactNode
  'aria-label'?: string
}

/**
 * Rows that each expand to show their content, reaching the edges of the
 * pane they sit in.
 */
export function ExpandableList({
  children,
  'aria-label': ariaLabel,
}: ExpandableListProps) {
  return (
    <div className={styles.root} role="list" aria-label={ariaLabel}>
      {children}
    </div>
  )
}

export interface ExpandableListItemProps {
  children?: ReactNode
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => unknown
  testId?: string
}

/**
 * A row of an {@link ExpandableList}: its content, then its panel.
 */
export function ExpandableListItem({
  children,
  isExpanded,
  onExpandedChange,
  testId,
}: ExpandableListItemProps) {
  const panelId = useId()
  return (
    <div role="listitem" className={styles.item} data-test={testId}>
      <DisclosureContext
        value={{
          isExpanded,
          toggle: () => onExpandedChange(!isExpanded),
          panelId,
        }}
      >
        {children}
      </DisclosureContext>
    </div>
  )
}

export interface ExpandableListItemContentProps {
  children?: ReactNode
  testId?: string
}

/**
 * The always visible part of a row, which toggles its panel.
 */
export function ExpandableListItemContent({
  children,
  testId,
}: ExpandableListItemContentProps) {
  const { isExpanded, toggle, panelId } = useDisclosureState()
  return (
    <RawButton
      className={cx(styles.itemContent, isExpanded && styles.expanded)}
      aria-expanded={isExpanded}
      aria-controls={panelId}
      onPress={toggle}
      testId={testId}
    >
      <Icon
        name="chevron-down"
        size={14}
        className={styles.arrow}
        aria-hidden="true"
      />
      <span className={styles.itemContentText}>{children}</span>
    </RawButton>
  )
}

export interface ExpandableListItemPanelProps {
  children?: ReactNode
}

/**
 * The part of a row shown while it is expanded.
 */
export function ExpandableListItemPanel({
  children,
}: ExpandableListItemPanelProps) {
  const { isExpanded, panelId } = useDisclosureState()
  return (
    isExpanded && (
      <div id={panelId} className={styles.panel}>
        {children}
      </div>
    )
  )
}

function useDisclosureState() {
  const state = use(DisclosureContext)
  if (!state) {
    throw new Error('Must be used within an ExpandableListItem.')
  }
  return state
}
